import React, { forwardRef, useMemo } from "react";
import moment from "moment";
import FormatNumber from "../../components/common/NumberFormat";
import { badge } from "../../components/common/Utils";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { GeoAlt, Phone } from "react-bootstrap-icons";
import useSelectorState from "../../hooks/useSelectorState";
import useISPowner from "../../hooks/useISPOwner";

const PrintCustomer = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const {
    page,
    printCopy,
    filterData,
    printOptions,
    customerData,
    currentCustomers,
  } = props;

  //---> Get user & current user data form useISPOwner hooks
  const { role, permission } = useISPowner();

  //---> Get redux store state data from useSelectorState hooks
  const { allPackages } = useSelectorState();

  //user Data
  const userData = useSelector((state) =>
    ["manager", "reseller", "collector"].includes(role)
      ? state.persistedReducer.auth.ispOwnerData
      : state.persistedReducer.auth.userData,
  );

  // get hotspot package
  const hotsPackage = useSelector((state) => state.hotspot?.package);

  //total monthly fee and due calculation
  const monthlyFee = useMemo(() => {
    let totalMonthlyFee = 0;

    page === "customer" &&
      currentCustomers?.map((item) => {
        // sum of all monthly fee
        totalMonthlyFee += item.monthlyFee;
      });

    return { totalMonthlyFee };
  }, [page, currentCustomers]);

  // customer current package find
  const getCustomerPackage = (value) => {
    if (value?.userType === "hotspot") {
      const findPack = hotsPackage.find(
        (item) => item.id === value?.hotspotPackage,
      );
      return findPack;
    } else {
      const findPack = allPackages.find(
        (item) => item.id === value?.mikrotikPackage,
      );
      return findPack;
    }
  };

  const renderDualColumn = (option1, option2, labelRenderer) => {
    if (option1?.checked && !option2?.checked) return labelRenderer(option1);
    if (!option1?.checked && option2?.checked) return labelRenderer(option2);
    if (option1?.checked && option2?.checked)
      return (
        <>
          {labelRenderer(option1)} / {labelRenderer(option2)}
        </>
      );
    return null;
  };

  const renderDualCell = (option1, option2, cellRenderer) => {
    if (option1?.checked && !option2?.checked) return cellRenderer("first");
    if (!option1?.checked && option2?.checked) return cellRenderer("second");
    if (option1?.checked && option2?.checked) return cellRenderer("both");
    return null;
  };

  return (
    <div ref={ref}>
      {page === "customer" && (
        <>
          {/* <div className="text-center bg-primary text-white fw-bold shadow-sm p-2 mb-2 rounded">
            <p>
              {t("companyName")} {userData.company}
            </p>
            {userData?.address && (
              <p>
                {t("address")} {userData?.address}
              </p>
            )}
          </div> */}

          {filterData && (
            <ul
              className="d-flex justify-content-evenly mb-1"
              style={{ fontSize: "12px" }}
            >
              {filterData?.area && (
                <li>
                  {t("area")}: {filterData?.area}
                </li>
              )}
              {filterData?.subArea && (
                <li>
                  {t("subArea")}: {filterData?.subArea}
                </li>
              )}
              {filterData?.status && (
                <li>
                  {t("status")}: {filterData?.status}
                </li>
              )}
              {filterData?.payment && (
                <li>
                  {t("paymentStatus")}: {filterData?.payment}
                </li>
              )}
              {filterData?.startDate && (
                <li>
                  {t("startDate")}:
                  {moment(filterData?.startDate).format("YYYY-MM-DD")}
                </li>
              )}
              {filterData?.endDate && (
                <li>
                  {t("endDate")}:
                  {moment(filterData?.endDate).format("YYYY-MM-DD")}
                </li>
              )}
              {filterData?.customerType && (
                <li>
                  {t("customerType")}: {filterData?.customerType}
                </li>
              )}
            </ul>
          )}

          <ul
            className="d-flex justify-content-evenly mb-1"
            style={{ fontSize: "12px" }}
          >
            <li>
              {t("totalCustomer")}: {currentCustomers.length}
            </li>
            <li>
              {t("totalMonthlyFee")}: ৳
              {FormatNumber(monthlyFee.totalMonthlyFee)}
            </li>
          </ul>

          <table className="table table-striped">
            <thead>
              {printOptions?.length > 0 && (
                <tr className="spetialSortingRow">
                  {printOptions[0]?.checked && (
                    <th>{t(printOptions[0].value)}</th>
                  )}

                  {/* Name / Username */}
                  <th>
                    {renderDualColumn(printOptions[1], printOptions[2], (opt) =>
                      t(opt.value),
                    )}
                  </th>

                  {/* Mobile / Address */}
                  <th>
                    {renderDualColumn(printOptions[3], printOptions[4], (opt) =>
                      t(opt.value),
                    )}
                  </th>

                  {/* Package */}
                  {printOptions[5]?.checked && (
                    <th>{t(printOptions[5].value)}</th>
                  )}

                  {/* Monthly Fee / Balance */}
                  <th>
                    {renderDualColumn(printOptions[6], printOptions[7], (opt) =>
                      t(opt.value),
                    )}
                  </th>

                  {/* BillingCycle / PromiseDate */}
                  <th>
                    {renderDualColumn(
                      printOptions[8],
                      printOptions[9],
                      (opt) => (
                        <>
                          {t(opt.value)} {t("date")}
                        </>
                      ),
                    )}
                  </th>

                  {/* PaymentStatus / Status */}
                  <th>
                    {renderDualColumn(
                      printOptions[10],
                      printOptions[11],
                      (opt) => t(opt.value),
                    )}
                  </th>

                  {/* CreatedAt */}
                  {printOptions[12]?.checked && (
                    <th>{t(printOptions[12].value)}</th>
                  )}
                  {printOptions[13]?.checked && (
                    <th>{t(printOptions[13].value)}</th>
                  )}
                </tr>
              )}
            </thead>

            <tbody>
              {printOptions?.length > 0 &&
                currentCustomers?.map((val, key) => (
                  <tr key={key} id={val.id}>
                    {/* Customer ID */}
                    {printOptions[0]?.checked && (
                      <td className="prin_td align-middle">
                        {val?.customerId}
                      </td>
                    )}

                    {/* Name / Username */}
                    <td className="prin_td align-middle">
                      {renderDualCell(
                        printOptions[1],
                        printOptions[2],
                        (mode) => {
                          if (mode === "first") return <p>{val.name}</p>;
                          if (mode === "second")
                            return (
                              <p>
                                {val?.userType === "pppoe"
                                  ? val?.pppoe?.name
                                  : val?.userType === "static"
                                    ? val?.queue?.target
                                    : val?.hotspot?.name}
                              </p>
                            );
                          if (mode === "both")
                            return (
                              <>
                                <p>{val.name}</p>
                                <p>
                                  {val?.userType === "pppoe"
                                    ? val?.pppoe?.name
                                    : val?.userType === "static"
                                      ? val?.queue?.target
                                      : val?.hotspot?.name}
                                </p>
                              </>
                            );
                        },
                      )}
                    </td>

                    {/* Mobile / Address */}
                    <td className="prin_td align-middle">
                      {renderDualCell(
                        printOptions[3],
                        printOptions[4],
                        (mode) => {
                          if (mode === "first")
                            return <p>{val?.mobile || "N/A"}</p>;
                          if (mode === "second")
                            return <p>{val?.address || "N/A"}</p>;
                          if (mode === "both")
                            return (
                              <>
                                <p>
                                  <Phone className="text-info" />{" "}
                                  {val?.mobile || "N/A"}
                                </p>
                                <p>
                                  <GeoAlt /> {val?.address || "N/A"}
                                </p>
                              </>
                            );
                        },
                      )}
                    </td>

                    {/* Package */}
                    {printOptions[5]?.checked && (
                      <td className="prin_td align-middle">
                        {getCustomerPackage(val)?.name}
                      </td>
                    )}

                    {/* Monthly Fee / Balance */}
                    <td className="prin_td align-middle">
                      {renderDualCell(
                        printOptions[6],
                        printOptions[7],
                        (mode) => {
                          if (mode === "first") return <p>৳{val.monthlyFee}</p>;
                          if (mode === "second")
                            return (
                              <p
                                className={`text-${
                                  val?.balance > -1 ? "success" : "danger"
                                }`}
                              >
                                ৳{val?.balance}
                              </p>
                            );
                          if (mode === "both")
                            return (
                              <>
                                <p>৳{val.monthlyFee}</p>
                                <p
                                  className={`text-${
                                    val?.balance > -1 ? "success" : "danger"
                                  }`}
                                >
                                  ৳{val?.balance}
                                </p>
                              </>
                            );
                        },
                      )}
                    </td>

                    {/* BillingCycle / PromiseDate */}
                    <td className="prin_td align-middle">
                      {renderDualCell(
                        printOptions[8],
                        printOptions[9],
                        (mode) => {
                          if (mode === "first")
                            return (
                              <p>
                                {moment(val?.billingCycle).format(
                                  "YYYY-MM-DD HH:mm a",
                                )}
                              </p>
                            );
                          if (
                            mode === "second" &&
                            (["ispOwner", "manager"].includes(role) ||
                              permission?.promiseDate)
                          )
                            return (
                              <p>
                                {moment(val?.promiseDate).format(
                                  "YYYY-MM-DD HH:mm a",
                                )}
                              </p>
                            );
                          if (mode === "both")
                            return (
                              <>
                                <p>
                                  {moment(val?.billingCycle).format(
                                    "YYYY-MM-DD HH:mm a",
                                  )}
                                </p>
                                {(["ispOwner", "manager"].includes(role) ||
                                  permission?.promiseDate) && (
                                  <p>
                                    {moment(val?.promiseDate).format(
                                      "YYYY-MM-DD HH:mm a",
                                    )}
                                  </p>
                                )}
                              </>
                            );
                        },
                      )}
                    </td>

                    {/* PaymentStatus / Status */}
                    <td className="prin_td align-middle">
                      {renderDualCell(
                        printOptions[10],
                        printOptions[11],
                        (mode) => {
                          if (mode === "first")
                            return <p>{badge(val?.paymentStatus)}</p>;
                          if (mode === "second")
                            return <p>{badge(val?.status)}</p>;
                          if (mode === "both")
                            return (
                              <div className="d-flex gap-2">
                                <p>{badge(val?.paymentStatus)}</p>
                                <p>{badge(val?.status)}</p>
                              </div>
                            );
                        },
                      )}
                    </td>

                    {/* CreatedAt */}
                    {printOptions[12]?.checked && (
                      <td className="prin_td align-middle">
                        {moment(val?.createdAt).format("YYYY-MM-DD")}
                      </td>
                    )}
                    {printOptions[13]?.checked && (
                      <td className="prin_td align-middle">
                        {moment(val?.createdAt).format("YYYY-MM-DD")}
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}

      {page === "billReport" && (
        <div>
          {printCopy === "both" && (
            <div>
              <div className="text-center mb-1">{t("officeCopy")}</div>
              <div
                className="text-center bg-primary text-white fw-bold p-1"
                style={{ borderRadius: "1.1rem" }}
              >
                <h2>{userData?.company}</h2>
                {userData?.address && (
                  <p>
                    {t("address")} : {userData?.address}
                  </p>
                )}
              </div>

              <div className="container">
                {printOptions?.length > 0 && (
                  <div className="displayGridHorizontalFill5_5">
                    {printOptions[0]?.checked && (
                      <p>
                        {t(printOptions[0]?.value)} :
                        <strong style={{ marginLeft: ".7rem" }}>
                          {customerData?.name}
                        </strong>
                      </p>
                    )}

                    {customerData?.mobile && printOptions[1]?.checked ? (
                      <p>
                        {t(printOptions[1]?.value)} :
                        <strong style={{ marginLeft: ".7rem" }}>
                          {customerData?.mobile}
                        </strong>
                      </p>
                    ) : (
                      ""
                    )}

                    {printOptions[2]?.checked && (
                      <p>
                        {t(printOptions[2]?.value)}:
                        <strong style={{ marginLeft: ".7rem" }}>
                          {customerData &&
                            getCustomerPackage(customerData)?.name}
                        </strong>
                      </p>
                    )}

                    {printOptions[3]?.checked && (
                      <p>
                        {t(printOptions[3]?.value)}:
                        <strong style={{ marginLeft: ".7rem" }}>
                          {customerData?.monthlyFee}
                        </strong>
                      </p>
                    )}

                    {customerData?.address && printOptions[4]?.checked ? (
                      <p>
                        <span>
                          {t(printOptions[4]?.value)} :{customerData?.address}
                        </span>
                      </p>
                    ) : (
                      ""
                    )}

                    <p>
                      {currentCustomers?.collectedBy && (
                        <span>
                          {t("billCollected")} : {currentCustomers?.name}
                        </span>
                      )}
                    </p>

                    <p>
                      {t("paidDate")}:
                      <strong style={{ marginLeft: ".7rem" }}>
                        {moment(currentCustomers?.createdAt).format(
                          "MMM DD YYYY",
                        )}
                      </strong>
                    </p>
                  </div>
                )}

                <table
                  className="table text-center align-center"
                  style={{ lineHeight: "7px" }}
                >
                  <tbody>
                    <tr>
                      <th>{t("billDhoron")}</th>
                      <td>
                        {currentCustomers?.billType == "bill"
                          ? t("bill")
                          : t("connectionFee")}
                      </td>
                    </tr>
                    <tr>
                      <th>{t("amount")}</th>
                      <td> {currentCustomers?.amount}</td>
                    </tr>
                    <tr>
                      <th>{t("due")}</th>
                      <td>{currentCustomers?.due}</td>
                    </tr>
                    <tr>
                      <th>{t("discount")}</th>
                      <td>{currentCustomers?.discount}</td>
                    </tr>
                    <tr>
                      <th>{t("medium")}</th>
                      <td>{currentCustomers?.medium}</td>
                    </tr>
                    <tr>
                      <th>{t("billingCycle")}</th>
                      <td>
                        {moment(
                          currentCustomers?.currentState.billingCycle,
                        ).format("MMM DD YYYY h:mm A")}
                      </td>
                    </tr>
                    <tr>
                      <th>{t("billComment")}</th>
                      <td>
                        {currentCustomers?.startDate && (
                          <span>
                            {moment(currentCustomers?.startDate).format(
                              "MMM DD YYYY",
                            )}
                            -
                          </span>
                        )}

                        {currentCustomers?.endDate && (
                          <span>
                            {moment(currentCustomers?.endDate).format(
                              "MMM DD YYYY",
                            )}
                          </span>
                        )}
                        <span>
                          {currentCustomers?.month} {currentCustomers?.note}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div
                  className="d-flex justify-content-between h5"
                  style={{ marginTop: "1.8rem" }}
                >
                  <div>
                    <hr className="mb-1" />
                    <p>{t("Proprietor")}</p>
                  </div>

                  <div>
                    <hr className="mb-1" />
                    <p>{t("customer")}</p>
                  </div>
                </div>
              </div>

              {/* hhhhhhhhhhhhhhhhhhh */}
              <div>
                <hr className="mb-1 mt-1" />
              </div>
            </div>
          )}

          <div>
            <div className="text-center mb-1 ">{t("customerCopy")}</div>
            <div
              className="text-center bg-primary text-white fw-bold p-1 "
              style={{ borderRadius: "1.1rem" }}
            >
              <h2>{userData?.company}</h2>
              {userData?.address && (
                <p>
                  {t("address")} : {userData?.address}
                </p>
              )}
            </div>

            <div className="container">
              <div className="container">
                {printOptions?.length > 0 && (
                  <div className="displayGridHorizontalFill5_5">
                    {printOptions[0]?.checked && (
                      <p>
                        {t(printOptions[0]?.value)} :
                        <strong style={{ marginLeft: ".7rem" }}>
                          {customerData?.name}
                        </strong>
                      </p>
                    )}

                    {customerData?.mobile && printOptions[1]?.checked ? (
                      <p>
                        {t(printOptions[1]?.value)} :
                        <strong style={{ marginLeft: ".7rem" }}>
                          {customerData?.mobile}
                        </strong>
                      </p>
                    ) : (
                      ""
                    )}

                    {printOptions[2]?.checked && (
                      <p>
                        {t(printOptions[2]?.value)}:
                        <strong style={{ marginLeft: ".7rem" }}>
                          {customerData &&
                            getCustomerPackage(customerData)?.name}
                        </strong>
                      </p>
                    )}

                    {printOptions[3]?.checked && (
                      <p>
                        {t(printOptions[3]?.value)}:
                        <strong style={{ marginLeft: ".7rem" }}>
                          {customerData?.monthlyFee}
                        </strong>
                      </p>
                    )}

                    {customerData?.address && printOptions[4]?.checked ? (
                      <p>
                        <span>
                          {t(printOptions[4]?.value)} :{customerData?.address}
                        </span>
                      </p>
                    ) : (
                      ""
                    )}

                    <p>
                      {currentCustomers?.collectedBy && (
                        <span>
                          {t("billCollected")} : {currentCustomers?.name}
                        </span>
                      )}
                    </p>

                    <p>
                      {t("paidDate")}:
                      <strong style={{ marginLeft: ".7rem" }}>
                        {moment(currentCustomers?.createdAt).format(
                          "MMM DD YYYY",
                        )}
                      </strong>
                    </p>
                  </div>
                )}

                <table
                  className="table text-center align-center"
                  style={{ lineHeight: "7px" }}
                >
                  <tbody>
                    <tr>
                      <th>{t("billDhoron")}</th>
                      <td>
                        {currentCustomers?.billType == "bill"
                          ? t("bill")
                          : t("connectionFee")}
                      </td>
                    </tr>
                    <tr>
                      <th>{t("amount")}</th>
                      <td> {currentCustomers?.amount}</td>
                    </tr>
                    <tr>
                      <th>{t("due")}</th>
                      <td>{currentCustomers?.due}</td>
                    </tr>
                    <tr>
                      <th>{t("discount")}</th>
                      <td>{currentCustomers?.discount}</td>
                    </tr>
                    <tr>
                      <th>{t("medium")}</th>
                      <td>{currentCustomers?.medium}</td>
                    </tr>
                    <tr>
                      <th>{t("billingCycle")}</th>
                      <td>
                        {moment(
                          currentCustomers?.currentState.billingCycle,
                        ).format("MMM DD YYYY h:mm A")}
                      </td>
                    </tr>
                    <tr>
                      <th>{t("billComment")}</th>
                      <td>
                        {currentCustomers?.startDate && (
                          <span>
                            {moment(currentCustomers?.startDate).format(
                              "MMM DD YYYY",
                            )}
                            -
                          </span>
                        )}

                        {currentCustomers?.endDate && (
                          <span>
                            {moment(currentCustomers?.endDate).format(
                              "MMM DD YYYY",
                            )}
                          </span>
                        )}
                        <span>
                          {currentCustomers?.month} {currentCustomers?.note}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div
                  className="d-flex justify-content-between h5"
                  style={{ marginTop: "1.8rem" }}
                >
                  <div>
                    <hr className="mb-1" />
                    <p>{t("Proprietor")}</p>
                  </div>

                  <div>
                    <hr className="mb-1" />
                    <p>{t("customer")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PrintCustomer;
